import React, { useState, useRef, useEffect } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, IosShare, PrintOutlined, SystemUpdateAlt } from "@mui/icons-material";
import { GridColDef, GridRowId, GridValidRowModel } from "@mui/x-data-grid";
import axios from "axios";
import FileSaver from "file-saver";
import moment from "moment";
import { useIsAdminMode } from "@/contexts";
import { useDataApi } from "@/hooks";
import { baseUrl } from "@/consts";
import {
  GetSuppliersAndCreatorsDto,
  Product,
  ProductCreator,
  ProductType,
  Supplier,
} from "@/interfaces";
import {
  addTimestampToUrl,
  combinePaths,
  formatAxiosError,
  formatCurrency,
  getBoolean,
  getInt,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString,
  isNumber,
} from "@/utils";
import { MuiHelper, ProductHelper } from "@/helpers";
import { DebounceInput, UploadFileInput } from "@/components/atoms";
import {
  AlertDialog,
  ProductAccordion,
  ProductAccordionRef,
  ProductEditDialog,
} from "@/components/organisms";
import { colors } from "@/theme";
import { CrudGridAdditionalAction } from "@/components/organisms/CrudGrid/interface";

type ProductCellProps = {
  src: string;
  alt: string;
  title?: string;
  width?: string;
  height?: string;
};

const ProductCell: React.FC<ProductCellProps> = ({
  src,
  alt,
  title,
  width,
  height,
  ...props
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "50px 2fr",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          alignSelf: "stretch",
        }}
        {...props}
      >
        {isNonEmptyString(src) ? (
          <img
            src={src}
            alt={alt}
            title={title}
            loading="lazy"
            style={{
              width: width || "100%",
              height: height || "auto",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : undefined}
      </Box>
      <Box sx={{ alignSelf: "center" }}>
        <Typography>{title}</Typography>
      </Box>
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const productRender = (params: any) => (
  <ProductCell
    src={params.row.productImageUrl}
    alt={params.row.productImageUrl}
    title={params.value}
    width="50px"
  />
);

type BarcodeCellProps = {
  src: string;
  alt: string;
  title?: string;
  width?: string;
  height?: string;
};

/** Fetches barcode image with JWT so it works when not on IP whitelist. */
const BarcodeCell: React.FC<BarcodeCellProps> = ({
  src,
  alt,
  title,
  width,
  height,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const isBarcodeApi = isNonEmptyString(src) && src.startsWith(baseUrl.BARCODE);

  useEffect(() => {
    if (!isNonEmptyString(src)) {
      setBlobUrl(null);
      setError(false);
      return;
    }
    if (!isBarcodeApi) {
      setBlobUrl(src);
      setError(false);
      return;
    }
    setBlobUrl(null);
    setError(false);
    let objectUrl: string | null = null;
    let revoked = false;
    axios
      .get(src, { responseType: "blob" })
      .then((res) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!revoked) setError(true);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const imgSrc = blobUrl && !error ? blobUrl : null;

  return (
    <Box
      sx={{
        width: "auto",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.white,
      }}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={alt}
          title={title}
          loading="lazy"
          style={{
            width: width || "100%",
            height: height || "auto",
            objectFit: "cover",
          }}
        />
      ) : error ? (
        <Typography variant="caption" color="text.secondary">
          Failed to load
        </Typography>
      ) : null}
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const barcodeRender = (params: any) => (
  <BarcodeCell
    src={ProductHelper.getBarcodeUrl(params.row)}
    alt={params.row.sku}
    title={params.row.sku}
    width="150px"
  />
);

const columns: GridColDef[] = [
  {
    field: "productDescription",
    headerName: "PRODUCT",
    flex: 2,
    renderCell: productRender,
  },
  {
    field: "inventoryCount",
    headerName: "INVENTORY QTY",
    flex: 1,
    type: "number",
  },
  {
    field: "price",
    headerName: "PRICE",
    flex: 1,
    type: "number",
    renderCell: (params) => formatCurrency(params.value),
  },
  { field: "sku", headerName: "SKU", flex: 1 },
  {
    field: "supplierName",
    headerName: "SUPPLIER",
    flex: 1,
  },
  {
    field: "productTypeName",
    headerName: "PRODUCT TYPE",
    flex: 1,
  },
  {
    field: "offsite",
    headerName: "FULFILLMENT",
    flex: 1,
    type: "boolean",
    renderCell: (params) =>
      getBoolean(params.value)
        ? ProductHelper.FULFILLMENT_OFF_SITE_STRING
        : ProductHelper.FULFILLMENT_IN_STORE_STRING,
  },
  {
    field: "barcode",
    headerName: "BARCODE",
    flex: 1,
    type: "custom",
    renderCell: barcodeRender,
  },
];

const exportFn = () => {
  axios
    .get(`${baseUrl.PRODUCT}/export`)
    .then((response) => {
      if (response.status === 200) {
        FileSaver.saveAs(
          new Blob([response?.data], { type: "text/csv;charset=utf-8" }),
          `Products-${moment().format("YYYY-MM-DD")}.csv`
        );
      } else {
        alert(`Error: ${response.statusText}`);
      }
    })
    .catch((error) => {
      alert(`Error: ${formatAxiosError(error)}`);
    });
};

const handlePrintClick = async (
  id: GridRowId,
  rows: readonly GridValidRowModel[]
) => {
  if (isNaN(id as number) || !isNonEmptyArray(rows as Product[])) {
    return;
  }

  const product = rows.find((item) => item.productId === id);
  if (!isNonEmptyObject(product) || !isNonEmptyString(product?.sku)) {
    return;
  }

  const barcodeUrl = ProductHelper.getBarcodeUrl(product as Product);
  if (!isNonEmptyString(barcodeUrl)) {
    return;
  }

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    return;
  }

  try {
    const response = await axios.get(barcodeUrl, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(response.data);
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Image</title>
        </head>
        <body onload="window.print();">
          <img src="${blobUrl}" style="max-width:100%;" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.addEventListener("afterprint", () => URL.revokeObjectURL(blobUrl));
  } catch {
    printWindow.close();
  }
};

const additionalAction = {
  additionalActionLabel: "Print",
  additionalActionIcon: () => {
    return <PrintOutlined />;
  },
  additionalActionHandler: handlePrintClick,
  position: "start",
} as CrudGridAdditionalAction;

/** Unique creators from suppliersandcreators (one accordion per creator). */
function getUniqueCreators(
  list?: GetSuppliersAndCreatorsDto[]
): ProductCreator[] {
  const arr = list ?? [];
  if (!isNonEmptyArray(arr)) return [];
  const byCreatedBy = new Map<string, ProductCreator>();
  arr.forEach((item) => {
    const raw = item.createdBy;
    if (!isNonEmptyString(raw)) return;
    const createdBy = raw as string;
    if (byCreatedBy.has(createdBy)) return;
    byCreatedBy.set(createdBy, {
      createdBy,
      creatorFirstName: item.creatorFirstName,
      creatorLastName: item.creatorLastName,
    });
  });
  return Array.from(byCreatedBy.values());
}

const Products: React.FC = () => {
  const { currentAdminData } = useIsAdminMode();
  const suppliersAndCreatorsUrl = combinePaths(
    baseUrl.SUPPLIER,
    "suppliersandcreators"
  );
  const {
    data: dataSupplier,
    isLoading: isLoadingSupplier,
    setUrl,
  } = useDataApi(suppliersAndCreatorsUrl);
  const { data: dataSuppliers } = useDataApi(baseUrl.SUPPLIER);
  const { data: dataProductType } = useDataApi(baseUrl.PRODUCT_TYPE);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [uploaded, setUploaded] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [isProductEditDialogOpen, setIsProductEditDialogOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<Product | undefined>(
    undefined
  );
  const [productListPdfUrl, setProductListPdfUrl] = useState<string | null>(null);
  const [isLoadingProductListPdf, setIsLoadingProductListPdf] = useState(false);
  const productListPdfUrlRef = useRef<string | null>(null);
  useEffect(() => {
    productListPdfUrlRef.current = productListPdfUrl;
  }, [productListPdfUrl]);
  useEffect(
    () => () => {
      const u = productListPdfUrlRef.current;
      if (u) URL.revokeObjectURL(u);
    },
    []
  );
  const uniqueCreators = getUniqueCreators(dataSupplier);
  const supplierList: Supplier[] = Array.isArray(dataSuppliers)
    ? dataSuppliers
    : (dataSuppliers as { data?: Supplier[] })?.data ?? [];
  const productTypeList: ProductType[] = Array.isArray(dataProductType?.data)
    ? dataProductType.data
    : [];
  const childRefs = useRef<{
    [key: string]: React.RefObject<ProductAccordionRef>;
  }>({});

  useEffect(() => {
    if (!uploaded || (uploaded as any)?.status !== 200) {
      return;
    }

    alert("Your products have been uploaded");
    setUploaded(undefined);
    setUrl(addTimestampToUrl(suppliersAndCreatorsUrl));
  }, [uploaded, setUrl, suppliersAndCreatorsUrl]);

  const createChildRefs = (creators?: ProductCreator[]) => {
    const list = creators ?? [];
    if (!isNonEmptyArray(list)) {
      return;
    }

    list.forEach((item) => {
      const key = item.createdBy;
      if (isNonEmptyString(key) && !childRefs.current[key]) {
        childRefs.current[key] = React.createRef<ProductAccordionRef>();
      }
    });
  };

  const reloadData = (createdBy?: string) => {
    if (!isNonEmptyString(createdBy) || !isNonEmptyObject(childRefs.current)) {
      return;
    }

    const childRef = childRefs.current[createdBy as string];

    if (childRef) {
      childRef.current?.reloadData(createdBy);
    } else {
      setUrl(addTimestampToUrl(suppliersAndCreatorsUrl));
    }
  };

  const editProduct = (product?: Product) => {
    if (isNumber(product?.productId)) {
      product!.updatedBy = currentAdminData?.email;
    } else {
      product = { createdBy: currentAdminData?.email } as Product;
    }

    setProductToEdit(product);
    setIsProductEditDialogOpen(true);
  };

  const onProductEditDialogClose = (product?: Product) => {
    setIsProductEditDialogOpen(false);
    setProductToEdit(undefined);

    if (product?.createdBy) {
      reloadData(product.createdBy);
    }
  };

  const closeProductListPdf = () => {
    setProductListPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const getProductList = async (
    creator?: ProductCreator,
    filter?: {
      supplierId?: string;
      productTypeId?: string;
      productIds?: number[];
    }
  ) => {
    if (!isNonEmptyObject(creator) || !isNonEmptyString(creator?.createdBy)) {
      return;
    }
    const createdBy = creator!.createdBy;
    const params = new URLSearchParams();
    if (isNonEmptyString(filter?.supplierId)) {
      params.append("supplierId", filter!.supplierId as string);
    }
    params.append("createdBy", createdBy);
    if (isNonEmptyString(filter?.productTypeId)) {
      params.append("productTypeId", filter!.productTypeId as string);
    }
    if (isNonEmptyArray(filter?.productIds)) {
      filter!.productIds!.forEach((id) => {
        params.append("productIds", String(id));
      });
    }
    const url = `${baseUrl.DOCUMENT}/getproductlist?${params.toString()}`;
    setIsLoadingProductListPdf(true);
    try {
      const { data, headers } = await axios.get<Blob>(url, {
        responseType: "blob",
      });
      const contentType = headers?.["content-type"] ?? "";
      const hasPdfContent = contentType.includes("application/pdf");
      const hasPayload = data && data.size > 0;
      if (!hasPayload || !hasPdfContent) {
        alert("Nothing to print for the selected filters.");
        return;
      }
      const blobUrl = URL.createObjectURL(data);
      setProductListPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return blobUrl;
      });
    } catch (e) {
      const message = formatAxiosError(e);
      alert(
        isNonEmptyString(message)
          ? message
          : "Nothing to print for the selected filters."
      );
    } finally {
      setIsLoadingProductListPdf(false);
    }
  };

  return (
    <Box>
      <Backdrop
        open={isLoadingProductListPdf}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={productListPdfUrl != null}
        onClose={closeProductListPdf}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Product list
          <IconButton onClick={closeProductListPdf} aria-label="Close PDF">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, overflow: "hidden" }}>
          {productListPdfUrl ? (
            <iframe
              title="Product list PDF"
              src={productListPdfUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <Box sx={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', px: 4, py: 4 }}>
        {MuiHelper.getHeader("Products")}
        <Box sx={{ justifySelf: "end" }}>
          <Button onClick={() => editProduct()} sx={{
            color: colors.white,
            background: colors.primaryOzone500,
            borderRadius: "10px",
            padding: "12px 24px",
            fontFamily: 'PF DIN Text Pro',
            fontSize: 16,
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              background: colors.ozone,
            },
          }}>
            Add Product
          </Button>
        </Box>
      </Box>
      <Box sx={{ backgroundColor: colors.neutralGrey300, p: 3, flex: 1 }}>
        <Box sx={{ backgroundColor: colors.white, p: 3, borderRadius: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 150px 100px",
              columnGap: "10px",
              justifyItems: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ justifySelf: "start", width: "30%" }}>
              <DebounceInput
                wait={500}
                placeholder="Search Product Name or SKU"
                searchIcon={true}
                searchIconPosition="start"
                variant="outlined"
                fullWidth
                onChange={(value, _) => setSearch(value)}
              />
            </Box>
            <Box>
              <UploadFileInput
                url={`${baseUrl.PRODUCT}/upload`}
                text="Bulk Import"
                endIcon={<SystemUpdateAlt />}
                additionalFormData={{ CreatedBy: currentAdminData?.email }}
                onUploaded={setUploaded}
                onError={setError}
                sxButton={{
                  fontFamily: 'PF DIN Text Pro',
                  fontSize: 16,
                  fontWeight: 500, color: colors.neutralGrey800,
                }}
              />
              {error ? (
                <AlertDialog
                  message={formatAxiosError(error)}
                  title="Error"
                  confirmText="Ok"
                  severity="error"
                />
              ) : null}
            </Box>
            <Box>
              <Button onClick={exportFn} sx={{
                color: colors.neutralGrey800,
                fontFamily: 'PF DIN Text Pro',
                fontSize: 16,
                fontWeight: 500,
              }} endIcon={<IosShare />}>
                Export
              </Button>
            </Box>
          </Box>
          {!isLoadingSupplier && isNonEmptyArray(uniqueCreators)
            ? uniqueCreators
                .filter((c): c is ProductCreator & { createdBy: string } =>
                  isNonEmptyString(c.createdBy)
                )
                .map((creator) => {
                  createChildRefs(uniqueCreators);

                  const refKey = creator.createdBy as string;
                  return (
                    <ProductAccordion
                      key={refKey}
                      ref={childRefs.current[refKey]}
                      creator={creator}
                    columns={columns}
                    getRowId={(row) => row.productId}
                    setRowId={(row, id) => {
                      row.productId = id;
                    }}
                    fieldToFocus="productDescription"
                    search={search}
                    additionalAction={additionalAction}
                    onEditRow={editProduct}
                    onProductList={getProductList}
                    suppliers={supplierList}
                    productTypes={productTypeList}
                  />
                );
              })
            : undefined}
        </Box>
      </Box>
      <ProductEditDialog
        product={productToEdit}
        suppliers={supplierList}
        productTypes={dataProductType?.data}
        open={isProductEditDialogOpen}
        onClose={onProductEditDialogClose}
      />
    </Box>
  );
};

export default Products;
